import * as m from '@staiter/i18n';
import type { ItemUpsertInput, ItemUsageResponse, ValuationMethod } from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Breadcrumbs, EmptyState, PageHeader, Table } from '~/components/data';
import { EntityAuditPanel } from '~/components/domain';
import { useToast } from '~/components/feedback';
import { ValuationMethodChange, VariantsMatrix } from '~/components/items';
import { Button, Dialog, Input, Select, Textarea } from '~/components/primitives';
import {
  deleteItem,
  fetchItem,
  fetchItemUsage,
  fetchItemUsedInBoms,
  updateItem,
} from '~/lib/api/inventory';
import { fetchStockBalance } from '~/lib/api/stock';
import { validateAuditHighlight } from '~/lib/audit/highlight-search';
import shared from './_auth._shared.module.css';
import styles from './_auth.items.$itemId.module.css';

export const Route = createFileRoute('/_auth/items/$itemId')({
  // F105 (С-I): `?highlight=<audit_event_id>` — запись журнала, которую открыть в панели истории.
  validateSearch: validateAuditHighlight,
  staticData: { minMainWidth: 960 },
  component: ItemDetail,
});

const TABS = ['general', 'variants', 'uom', 'used_in_boms', 'stock', 'valuation'] as const;
type ItemTab = (typeof TABS)[number];
function tabLabel(tab: ItemTab): string {
  switch (tab) {
    case 'general':
      return m.route_item_detail_tab_general();
    case 'variants':
      return m.route_item_detail_tab_variants();
    case 'uom':
      return m.route_item_detail_tab_uom();
    case 'used_in_boms':
      return m.route_item_detail_tab_used_in_boms();
    case 'stock':
      return m.route_item_detail_tab_stock();
    case 'valuation':
      return m.route_item_detail_tab_valuation();
  }
}

function ItemDetail(): JSX.Element {
  const { itemId } = useParams({ from: '/_auth/items/$itemId' });
  const { highlight: auditHighlight } = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { data: item } = useQuery({
    queryKey: ['items', itemId],
    queryFn: () => fetchItem(itemId),
  });
  const [tab, setTab] = useState<ItemTab>('general');
  const [valuationOpen, setValuationOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [usageData, setUsageData] = useState<ItemUsageResponse | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);

  const currentMethod: ValuationMethod =
    (item as unknown as { valuation_method?: ValuationMethod })?.valuation_method ?? 'moving_avg';

  const deleteMut = useMutation({
    mutationFn: () => deleteItem(itemId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['items'] });
      toast.push({ variant: 'success', title: m.route_item_detail_toast_deleted() });
      navigate({ to: '/items' });
    },
    onError: (err: Error) => {
      toast.push({
        variant: 'error',
        title: m.route_item_detail_toast_delete_failed(),
        description: err.message,
      });
    },
  });

  async function handleDeleteClick(): Promise<void> {
    setUsageLoading(true);
    try {
      const usage = await fetchItemUsage(itemId);
      setUsageData(usage);
    } catch {
      setUsageData(null);
    } finally {
      setUsageLoading(false);
      setDeleteOpen(true);
    }
  }

  return (
    <div className={shared.page}>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[{ label: m.nav_items(), to: '/items' }, { label: item ? item.sku : '…' }]}
          />
        }
        title={
          item
            ? m.route_item_detail_title_with_sku({ sku: item.sku, name: item.name })
            : m.route_item_detail_title_fallback()
        }
        description={
          item
            ? m.route_item_detail_description({
                // FE-286 Pass 58 follow-up: subtitle тоже localize raw enum.
                type:
                  itemTypeOptions().find((o) => o.value === item.item_type)?.label ??
                  item.item_type ??
                  '',
                uom: item.default_uom,
              })
            : undefined
        }
        actions={
          <>
            <Button variant="secondary" onClick={() => setEditing(!editing)}>
              {editing ? m.route_item_detail_btn_cancel() : m.route_item_detail_btn_edit()}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              leadingIcon={<Trash2 size={14} />}
              onClick={handleDeleteClick}
              loading={usageLoading}
            >
              {m.route_item_detail_btn_delete()}
            </Button>
          </>
        }
      />

      {deleteOpen && (
        <Dialog
          open
          onClose={() => setDeleteOpen(false)}
          title={m.route_item_detail_delete_title()}
          size="md"
          footer={
            <>
              <Button variant="ghost" onClick={() => setDeleteOpen(false)}>
                {m.route_item_detail_btn_cancel()}
              </Button>
              <Button
                variant="destructive"
                disabled={usageData != null && !usageData.can_delete}
                loading={deleteMut.isPending}
                onClick={() => deleteMut.mutate()}
              >
                {m.route_item_detail_btn_delete()}
              </Button>
            </>
          }
        >
          {usageData ? (
            <div className={styles.deleteUsage}>
              {!usageData.can_delete && (
                <div className={[shared.inlineAlert, shared.inlineAlertDanger].join(' ')}>
                  {m.route_item_detail_delete_blocked()}
                </div>
              )}
              <dl className={styles.usageGrid}>
                <dt>{m.route_item_detail_usage_bom()}</dt>
                <dd className={shared.mono}>{usageData.bom_count}</dd>
                <dt>{m.route_item_detail_usage_batch()}</dt>
                <dd className={shared.mono}>{usageData.batch_count}</dd>
                <dt>{m.route_item_detail_usage_stock_entry()}</dt>
                <dd className={shared.mono}>{usageData.stock_entry_count}</dd>
                <dt>{m.route_item_detail_usage_sle()}</dt>
                <dd className={shared.mono}>{usageData.sle_count}</dd>
              </dl>
              {usageData.can_delete && <p>{m.route_item_detail_delete_confirm()}</p>}
            </div>
          ) : (
            <p>{m.route_item_detail_delete_no_usage()}</p>
          )}
        </Dialog>
      )}
      <nav className={shared.tabs} role="tablist">
        {TABS.map((t) => (
          <button
            type="button"
            key={t}
            role="tab"
            aria-selected={tab === t}
            className={[shared.tab, tab === t ? shared.tabActive : ''].filter(Boolean).join(' ')}
            onClick={() => setTab(t)}
          >
            {tabLabel(t)}
          </button>
        ))}
      </nav>
      {tab === 'general' && item && (
        <GeneralTab
          item={item}
          itemId={itemId}
          editing={editing}
          onSaved={() => setEditing(false)}
        />
      )}
      {tab === 'variants' && <VariantsMatrix itemId={itemId} />}
      {tab === 'uom' && <EmptyState title={m.route_item_detail_uom_placeholder()} />}
      {tab === 'used_in_boms' && <UsedInBomsTab itemId={itemId} />}
      {tab === 'stock' && <StockBalanceTab itemId={itemId} />}
      {tab === 'valuation' && (
        <ValuationTab currentMethod={currentMethod} onChange={() => setValuationOpen(true)} />
      )}
      <EntityAuditPanel entity_type="item" entity_id={itemId} highlight={auditHighlight} />
      <ValuationMethodChange
        open={valuationOpen}
        onClose={() => setValuationOpen(false)}
        itemId={itemId}
        currentMethod={currentMethod}
      />
    </div>
  );
}

function itemTypeOptions(): { value: string; label: string }[] {
  return [
    { value: 'raw_material', label: m.route_item_detail_type_raw_material() },
    { value: 'sub_assembly', label: m.route_item_detail_type_sub_assembly() },
    { value: 'finished_good', label: m.route_item_detail_type_finished_good() },
    { value: 'consumable', label: m.route_item_detail_type_consumable() },
    { value: 'service', label: m.route_item_detail_type_service() },
  ];
}

function GeneralTab({
  item,
  itemId,
  editing,
  onSaved,
}: {
  item: {
    sku: string;
    name: string;
    item_type?: string;
    default_uom: string;
    source_system: string;
    is_stock_item?: boolean;
  };
  itemId: string;
  editing: boolean;
  onSaved: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(item.name);
  const [itemType, setItemType] = useState(item.item_type ?? 'raw_material');
  const [defaultUom, setDefaultUom] = useState(item.default_uom);
  const [description, setDescription] = useState('');

  useEffect(() => {
    setName(item.name);
    setItemType(item.item_type ?? 'raw_material');
    setDefaultUom(item.default_uom);
  }, [item.name, item.item_type, item.default_uom]);

  const saveMut = useMutation({
    mutationFn: () =>
      updateItem(itemId, {
        name,
        item_type: itemType as ItemUpsertInput['item_type'],
        default_uom: defaultUom,
        ...(description ? { description } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['items', itemId] });
      void qc.invalidateQueries({ queryKey: ['items'] });
      toast.push({ variant: 'success', title: m.route_item_detail_toast_saved() });
      onSaved();
    },
    onError: (err: Error) => {
      toast.push({
        variant: 'error',
        title: m.route_item_detail_toast_save_failed(),
        description: err.message,
      });
    },
  });

  if (!editing) {
    return (
      <section className={styles.viewCard}>
        <dl className={styles.viewGrid}>
          <dt>{m.route_item_detail_field_sku()}</dt>
          <dd className={shared.mono}>{item.sku}</dd>
          <dt>{m.route_item_detail_field_name()}</dt>
          <dd>{item.name}</dd>
          <dt>{m.route_item_detail_field_type()}</dt>
          {/* FE-286 Pass 58: localize item_type через itemTypeOptions labels вместо raw enum. */}
          <dd>
            {itemTypeOptions().find((o) => o.value === item.item_type)?.label ?? item.item_type}
          </dd>
          <dt>{m.route_item_detail_field_default_uom()}</dt>
          <dd className={shared.mono}>{item.default_uom}</dd>
          <dt>{m.route_item_detail_field_source()}</dt>
          <dd className={shared.mono}>{item.source_system}</dd>
          <dt>{m.route_item_detail_field_is_stock_item()}</dt>
          <dd>{item.is_stock_item ? m.route_item_detail_yes() : m.route_item_detail_no()}</dd>
        </dl>
      </section>
    );
  }

  return (
    <section className={styles.editCard}>
      <div className={shared.formGrid2}>
        <Input
          label={m.route_item_detail_field_sku()}
          value={item.sku}
          disabled
          hint={m.route_item_detail_field_not_editable()}
          className={shared.mono}
        />
        <Input
          label={m.route_item_detail_field_name()}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Select
          label={m.route_item_detail_field_type()}
          value={itemType}
          onChange={(e) => setItemType(e.target.value)}
          options={itemTypeOptions()}
        />
        <Input
          label={m.route_item_detail_field_default_uom_edit()}
          value={defaultUom}
          onChange={(e) => setDefaultUom(e.target.value)}
          className={shared.mono}
        />
        <Input
          label={m.route_item_detail_field_source()}
          value={item.source_system}
          disabled
          hint={m.route_item_detail_field_not_editable()}
          className={shared.mono}
        />
        <Textarea
          label={m.route_item_detail_field_description()}
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={m.route_item_detail_field_description_placeholder()}
        />
      </div>
      <div className={styles.editActions}>
        <Button variant="primary" loading={saveMut.isPending} onClick={() => saveMut.mutate()}>
          {m.route_item_detail_btn_save()}
        </Button>
        <Button variant="ghost" onClick={onSaved}>
          {m.route_item_detail_btn_cancel()}
        </Button>
      </div>
    </section>
  );
}

function ValuationTab({
  currentMethod,
  onChange,
}: {
  currentMethod: ValuationMethod;
  onChange: () => void;
}): JSX.Element {
  const methodLabel: Record<ValuationMethod, string> = {
    moving_avg: m.route_item_detail_valuation_moving_avg(),
    fifo: m.route_item_detail_valuation_fifo(),
    lifo: m.route_item_detail_valuation_lifo(),
  };
  return (
    <section className={styles.valuationCard}>
      <dl className={styles.viewGrid}>
        <dt>{m.route_item_detail_valuation_current()}</dt>
        <dd className={shared.mono}>{methodLabel[currentMethod]}</dd>
      </dl>
      <div>
        <Button variant="primary" onClick={onChange}>
          {m.route_item_detail_valuation_btn_change()}
        </Button>
        <p className={styles.valuationHint}>{m.route_item_detail_valuation_hint()}</p>
      </div>
    </section>
  );
}

function UsedInBomsTab({ itemId }: { itemId: string }): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ['items', itemId, 'used-in-boms'],
    queryFn: () => fetchItemUsedInBoms(itemId),
  });
  if (!isLoading && (data?.items.length ?? 0) === 0) {
    return (
      <EmptyState
        title={m.route_item_detail_used_in_boms_empty()}
        action={
          <Link to="/boms" className={shared.link}>
            {m.route_item_detail_used_in_boms_link()}
          </Link>
        }
      />
    );
  }
  return (
    <Table
      ariaLabel={m.route_item_detail_used_in_boms_aria()}
      loading={isLoading}
      data={data?.items ?? []}
      rowKey={(b) => b.id}
      columns={[
        {
          id: 'code',
          header: m.route_item_detail_col_bom(),
          accessor: (b) => (
            <Link to="/boms/$bomId" params={{ bomId: b.id }} className={shared.link}>
              <span className={shared.mono}>{b.code}</span>
            </Link>
          ),
        },
        {
          id: 'version',
          header: m.route_item_detail_col_version(),
          accessor: (b) => b.version,
          mono: true,
        },
        {
          id: 'active',
          header: m.route_item_detail_col_active(),
          accessor: (b) => (b.is_active ? '✓' : '—'),
          mono: true,
        },
        {
          id: 'qty',
          header: m.route_item_detail_col_qty_used(),
          accessor: (b) => b.qty_used,
          mono: true,
        },
        {
          id: 'scrap',
          header: m.route_item_detail_col_scrap_pct(),
          accessor: (b) => m.route_item_detail_col_scrap_pct_val({ pct: String(b.scrap_pct) }),
          mono: true,
        },
        {
          id: 'depth',
          header: m.route_item_detail_col_depth(),
          accessor: (b) => b.depth,
          mono: true,
        },
      ]}
    />
  );
}

function StockBalanceTab({ itemId }: { itemId: string }): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ['stock', 'balance', { item: itemId, group_by: 'warehouse' }],
    queryFn: () => fetchStockBalance({ item: itemId, group_by: 'warehouse' }),
  });
  return (
    <Table
      ariaLabel={m.route_item_detail_stock_aria()}
      loading={isLoading}
      data={data?.items ?? []}
      rowKey={(r) => `${r.warehouse_id}-${r.batch_no ?? 'none'}`}
      columns={[
        {
          id: 'wh',
          header: m.route_item_detail_col_wh(),
          accessor: (r) => r.warehouse_code,
          mono: true,
        },
        {
          id: 'batch',
          header: m.route_item_detail_col_batch(),
          accessor: (r) => r.batch_no ?? '—',
          mono: true,
        },
        {
          id: 'qty',
          header: m.route_item_detail_col_qty(),
          accessor: (r) => r.qty,
          mono: true,
          align: 'right',
        },
        {
          id: 'rate',
          header: m.route_item_detail_col_rate(),
          accessor: (r) => r.valuation_rate ?? '—',
          mono: true,
          align: 'right',
        },
        {
          id: 'value',
          header: m.route_item_detail_col_value(),
          accessor: (r) => r.stock_value ?? '—',
          mono: true,
          align: 'right',
        },
      ]}
    />
  );
}
